// Presentation copy for the hero screens and skill tooltips: each hero's Q, W and passive description. The prose
// is a template per language (i18n dictionaries, keys skill.<hero>.<Q|W>.desc and passive.<hero>.desc); every
// number is read here from core's SKILLS/PASSIVES, so a balance change can never make either language lie.
import type { HeroKind } from '../core/heroes.js';
import type { SkillKey } from '../core/commands.js';
import { SKILLS } from '../core/skills.js';
import { PASSIVES } from '../core/passives.js';
import { RULES } from '../core/game.js';
import { SUMMONS } from '../core/summons.js';
import { t, type Params } from './i18n/index.js';

export type HeroInfo = { Q: string; W: string };

const pct = (x: number) => `${Math.round(x * 100)}%`;
const deg = (rad: number) => `${Math.round(rad * 180 / Math.PI)}°`;

const S = SKILLS;

const SKILL_PARAMS: Record<HeroKind, Record<SkillKey, Params>> = {
  rhino: {
    Q: { range: S.rhino.Q.range, dmg: S.rhino.Q.dmg, stun: S.rhino.Q.stun, wallStun: S.rhino.Q.wallStun },
    W: { dur: S.rhino.W.duration, arc: deg(S.rhino.W.arc * 2), slow: pct(S.rhino.W.slow), heal: pct(S.rhino.W.healPct), stun: S.rhino.W.empoweredStun },
  },
  durer: {
    Q: { r: S.durer.Q.r, taunt: S.durer.Q.taunt },
    W: { width: S.durer.W.half * 2, life: S.durer.W.life },
  },
  landsknecht: {
    Q: { arc: deg(S.landsknecht.Q.arc * 2), range: S.landsknecht.Q.range, dmg: S.landsknecht.Q.dmg },
    W: { r: S.landsknecht.W.r, haste: pct(S.landsknecht.W.haste), life: S.landsknecht.W.life },
  },
  arquebusier: {
    Q: { charge: S.arquebusier.Q.maxCharge, slow: pct(S.arquebusier.Q.slow), dmgMin: S.arquebusier.Q.dmg[0], dmgMax: S.arquebusier.Q.dmg[1],
         rangeMin: S.arquebusier.Q.range[0], rangeMax: S.arquebusier.Q.range[1] },
    W: { range: S.arquebusier.W.range, arm: S.arquebusier.W.trap.arm, life: S.arquebusier.W.trap.life, dmg: S.arquebusier.W.trap.dmg,
         slow: pct(S.arquebusier.W.trap.slow), slowFor: S.arquebusier.W.trap.slowFor },
  },
  astrologer: {
    Q: { range: S.astrologer.Q.range, dmg: S.astrologer.Q.dmg },
    W: { range: S.astrologer.W.range, r: S.astrologer.W.r, life: S.astrologer.W.life, drain: pct(S.astrologer.W.hpPerSec) },
  },
  salamander: {
    Q: { range: S.salamander.Q.range, arc: deg(S.salamander.Q.arc * 2), life: S.salamander.Q.life, dps: S.salamander.Q.dps, slow: pct(S.salamander.Q.slow) },
    W: { range: S.salamander.W.range, r: S.salamander.W.pool.r, dps: S.salamander.W.pool.dps, life: S.salamander.W.pool.life },
  },
  death: {
    Q: { range: S.death.Q.range, dmg: S.death.Q.dmg, window: S.death.Q.chainWindow },
    W: { range: S.death.W.range, invuln: S.death.W.invuln, mult: S.death.Q.empowered },
  },
  ferryman: {
    Q: { range: S.ferryman.Q.range, dmg: S.ferryman.Q.dmg, stun: S.ferryman.Q.stun },
    W: { r: S.ferryman.W.r, life: S.ferryman.W.life, slow: pct(S.ferryman.W.slow) },
  },
  devil: {
    Q: { range: S.devil.Q.range, r: S.devil.Q.r, dmg: S.devil.Q.dmg, slow: pct(S.devil.Q.slow), slowFor: S.devil.Q.slowFor },
    W: { cost: pct(S.devil.W.cost), life: S.devil.W.life, bonus: pct(S.devil.W.mult - 1) },
  },
  plague: {
    Q: { range: S.plague.Q.range, r: S.plague.Q.r, life: S.plague.Q.life, heal: S.plague.Q.heal, dmg: S.plague.Q.dmg, slow: pct(S.plague.Q.slow) },
    W: { shield: S.plague.W.shield, range: S.plague.W.range, life: S.plague.W.life, refund: pct(S.plague.W.refund) },
  },
  monk: {
    Q: { range: S.monk.Q.range, total: S.monk.Q.total, life: S.monk.Q.life },
    W: { r: S.monk.W.r, life: S.monk.W.life },
  },
  grotesque: {
    Q: { r: S.grotesque.Q.r, speed: S.grotesque.Q.speed, life: S.grotesque.Q.life, dps: S.grotesque.Q.dps },
    W: { r: S.grotesque.W.r, dmg: S.grotesque.W.dmg },
  },
  shadow: {
    Q: { life: S.shadow.Q.life, hits: S.shadow.Q.ambushHits, window: S.shadow.Q.ambushWindow, mult: S.shadow.Q.ambush, regen: S.shadow.Q.regenPct * 100 },
    W: { range: S.shadow.W.range, delay: S.shadow.W.delay, r: S.shadow.W.r, dmg: S.shadow.W.dmg },
  },
  sin: {
    Q: { range: S.sin.Q.range, life: S.sin.Q.life, drain: pct(S.sin.Q.drain) },
    W: { life: S.sin.W.life, r: S.sin.W.r, taken: pct(1 - S.sin.W.share), self: pct(S.sin.W.taken) },
  },
  nightmare: {
    Q: { range: S.nightmare.Q.range, r: S.nightmare.Q.r, fear: S.nightmare.Q.fear },
    W: { life: S.nightmare.W.life, r: S.nightmare.W.r, chance: pct(S.nightmare.W.chance), mult: pct(S.nightmare.W.mult) },
  },
  birdcatcher: {
    Q: { count: S.birdcatcher.Q.count, life: S.birdcatcher.Q.life, hp: SUMMONS.magpie.hp, dmg: SUMMONS.magpie.dmg, delay: S.birdcatcher.Q.peckDelay },
    W: { range: S.birdcatcher.W.range, speed: S.birdcatcher.W.speed, dmg: S.birdcatcher.W.dmg, slow: pct(S.birdcatcher.W.slow), slowFor: S.birdcatcher.W.slowFor },
  },
  puppeteer: {
    Q: { range: S.puppeteer.Q.range, life: S.puppeteer.Q.life, hp: SUMMONS.golem.hp, aggro: SUMMONS.golem.aggro, dmg: SUMMONS.golem.dmg },
    W: { range: S.puppeteer.W.range, r: S.puppeteer.W.r, life: S.puppeteer.W.life },
  },
  shewolf: {
    Q: { count: S.shewolf.Q.count, life: S.shewolf.Q.life, hp: SUMMONS.wolf.hp, dmg: SUMMONS.wolf.dmg },
    W: { life: S.shewolf.W.life, haste: pct(S.shewolf.W.haste), range: S.shewolf.W.range, bonus: pct(S.shewolf.W.mult - 1) },
  },
};

const P = PASSIVES;

const PASSIVE_PARAMS: Record<HeroKind, Params> = {
  rhino: { mult: pct(P.rhino.mult) },
  durer: { r: P.durer.r, reduce: pct(P.durer.reduce) },
  landsknecht: { arc: deg(P.landsknecht.arc), share: pct(P.landsknecht.share) },
  arquebusier: { still: P.arquebusier.still, bonus: P.arquebusier.bonus },
  astrologer: { reveal: P.astrologer.reveal },
  salamander: { burn: P.salamander.ticks * P.salamander.interval, dps: P.salamander.dps },
  death: { bonus: pct(P.death.bonus), below: pct(P.death.below) },
  ferryman: { slow: pct(P.ferryman.slow), slowFor: P.ferryman.slowFor },
  devil: { leech: pct(P.devil.leech) },
  plague: { bonus: pct(P.plague.bonus), below: pct(P.plague.below) },
  monk: { cut: pct(1 - P.monk.mult), regen: P.monk.regen * 100, base: RULES.baseRegen * 100 },
  grotesque: { dmg: P.grotesque.dmg },
  shadow: { revealFor: P.shadow.revealFor, stillFor: P.shadow.stillFor },
  sin: { window: P.sin.window, bonus: P.sin.bonus },
  nightmare: { r: P.nightmare.r },
  birdcatcher: { sight: P.birdcatcher.sight },
  puppeteer: { shield: pct(P.puppeteer.shield), life: P.puppeteer.life },
  shewolf: { r: P.shewolf.r, reduce: pct(P.shewolf.reduce) },
};

export function skillDesc(kind: HeroKind, key: SkillKey): string {
  return t(`skill.${kind}.${key}.desc` as const, SKILL_PARAMS[kind][key]);
}

export function heroInfo(kind: HeroKind): HeroInfo {
  return { Q: skillDesc(kind, 'Q'), W: skillDesc(kind, 'W') };
}

export function passiveInfo(kind: HeroKind): string {
  return t(`passive.${kind}.desc` as const, PASSIVE_PARAMS[kind]);
}
